/**
 * Underwriting math for the admin loan review: housing payment, DTI (front /
 * back), LTV, reserves for agency files; DSCR, LTC, ARV-LTV, experience tier
 * and project P&L for investor files.
 *
 * These are review aids computed from what the borrower entered — not a
 * decision. Benchmarks are common guides, labelled as such in the UI.
 */
import type {
  LoanApplication,
  LoanAssetLine,
  LoanEmploymentRow,
} from '@/types/LoanApplicationType';

export type RatioStatus = 'good' | 'caution' | 'high' | 'unknown';

export interface ReviewScenario {
  purchasePrice: number | null;
  loanAmount: number | null;
  interestRatePct: number | null;
  termMonths: number;
  /** Interest-only payment (typical for bridge / fix-and-flip). */
  interestOnly: boolean;
  /** Estimated closing costs as % of price, for cash-to-close. */
  closingCostPct: number;
}

/** Conservative haircut for retirement funds when counting liquidity. */
export const RETIREMENT_HAIRCUT = 0.6;
/** Fannie Mae-style vacancy / maintenance factor on gross rent. */
export const RENTAL_INCOME_FACTOR = 0.75;
/** Selling costs assumed on a flip exit, % of ARV. */
export const SELLING_COST_PCT = 6;
/** Window for counting recent investor experience. */
export const EXPERIENCE_LOOKBACK_MONTHS = 36;

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const product = (app: LoanApplication) =>
  app.sourceProductId && typeof app.sourceProductId === 'object'
    ? app.sourceProductId
    : null;

export function isInvestorFile(app: LoanApplication): boolean {
  return (
    app.flowProfile === 'investor_dscr' ||
    (app.flowProfile as string) === 'commercial' ||
    app.productType === 'dscr' ||
    app.productType === 'bridge'
  );
}

export function isRefinanceFile(app: LoanApplication): boolean {
  const purpose = String(app.agencyFields?.loanPurpose || '').toLowerCase();
  if (purpose.includes('refi')) return true;
  if (purpose.includes('purchase')) return false;
  return (
    app.flowProfile === 'refinance' ||
    app.applicationKind === 'refinance_property'
  );
}

export type Occupancy = 'primary' | 'second' | 'investment';

export function occupancyOf(app: LoanApplication): Occupancy {
  const occ = String(app.agencyFields?.occupancyType || '').toLowerCase();
  if (occ.includes('second')) return 'second';
  if (occ.includes('invest')) return 'investment';
  if (occ.includes('primary') || occ.includes('owner')) return 'primary';
  return isInvestorFile(app) ? 'investment' : 'primary';
}

/** Scenario prefilled from the file; the reviewer can override any of it. */
export function defaultScenario(app: LoanApplication): ReviewScenario {
  const agency = app.agencyFields || {};
  const dscr = app.dscrFields || ({} as LoanApplication['dscrFields']);
  const p = product(app);
  const loanInfo = (p?.loanInfo || {}) as Record<string, unknown>;
  const termMonths =
    (num(agency.loanTermYears) ?? 0) * 12 ||
    num(dscr?.amortizationMonths) ||
    360;
  return {
    purchasePrice:
      num(loanInfo.offerPrice) ??
      num(p?.price) ??
      num(agency.estimatedPropertyValue),
    loanAmount:
      num(agency.loanAmount) ??
      num(dscr?.loanAmount) ??
      num(app.refinanceFields?.requestedLoanAmount),
    interestRatePct:
      num((agency as Record<string, unknown>).interestRatePct) ??
      num(dscr?.interestRateAnnualPct),
    termMonths,
    interestOnly: app.productType === 'bridge',
    closingCostPct: 3,
  };
}

/** Monthly P&I (or interest-only) payment. */
export function monthlyPayment(
  loanAmount: number | null,
  annualRatePct: number | null,
  termMonths: number,
  interestOnly = false
): number | null {
  if (!loanAmount || annualRatePct === null || !termMonths) return null;
  const i = annualRatePct / 100 / 12;
  if (interestOnly) return loanAmount * i;
  if (i === 0) return loanAmount / termMonths;
  return (loanAmount * i) / (1 - (1 + i) ** -termMonths);
}

export interface HousingExpense {
  principalInterest: number | null;
  taxes: number;
  insurance: number;
  hoa: number;
  mortgageInsurance: number;
  /** PITIA: P&I + taxes + insurance + HOA + MI. */
  total: number | null;
}

export function housingExpense(
  app: LoanApplication,
  s: ReviewScenario
): HousingExpense {
  const prop = app.property || ({} as LoanApplication['property']);
  const dscr = app.dscrFields || ({} as LoanApplication['dscrFields']);
  const loanInfo = (product(app)?.loanInfo || {}) as Record<string, unknown>;
  const pi = monthlyPayment(
    s.loanAmount,
    s.interestRatePct,
    s.termMonths,
    s.interestOnly
  );
  const taxes =
    num(prop?.proposedMonthlyTaxes) ??
    num(dscr?.monthlyTaxes) ??
    num(loanInfo.monthlyPropertyTaxes) ??
    0;
  const insurance =
    num(prop?.proposedMonthlyInsurance) ??
    num(dscr?.monthlyInsurance) ??
    num(loanInfo.monthlyHomeownersInsurance) ??
    0;
  const hoa =
    num(prop?.proposedMonthlyHoaDues) ??
    num(dscr?.monthlyHoa) ??
    num(loanInfo.hoaFees) ??
    0;
  const mi = num(app.agencyFields?.monthlyMiEstimate) ?? 0;
  return {
    principalInterest: pi,
    taxes,
    insurance,
    hoa,
    mortgageInsurance: mi,
    total: pi === null ? null : pi + taxes + insurance + hoa + mi,
  };
}

/* ------------------------------------------------------------------ income */

export interface IncomeRow {
  employerName: string;
  jobTitle: string;
  startDate: string;
  tenureMonths: number | null;
  isCurrent: boolean;
  base: number;
  overtime: number;
  bonus: number;
  total: number;
}

export interface IncomeSummary {
  rows: IncomeRow[];
  /** Current employment only — what qualifies. */
  base: number;
  variable: number;
  employmentMonthly: number;
  /** Months of employment history covered by the rows (capped at today). */
  historyMonths: number;
  selfEmployed: boolean;
}

/**
 * Parses a date, treating date-only strings ("2024-01-15") as local calendar
 * dates — `new Date('2024-01-15')` is UTC midnight, which is the previous day
 * in US time zones and skews month counts.
 */
export function parseDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function monthsBetween(
  from: string | Date | null | undefined,
  to: string | Date
): number | null {
  const d = parseDate(from);
  const end = parseDate(to);
  if (!d || !end) return null;
  let m =
    (end.getFullYear() - d.getFullYear()) * 12 +
    (end.getMonth() - d.getMonth());
  if (end.getDate() < d.getDate()) m -= 1;
  return Math.max(0, m);
}

function incomeRow(row: LoanEmploymentRow, now: Date): IncomeRow {
  const total = num(row.monthlyIncome) ?? 0;
  const overtime = num(row.monthlyOvertime) ?? 0;
  const bonus = num(row.monthlyBonus) ?? 0;
  const base =
    num(row.monthlyBaseIncome) ?? Math.max(total - overtime - bonus, 0);
  return {
    employerName: row.employerName || '—',
    jobTitle: row.jobTitle || '',
    startDate: row.startDate || '',
    tenureMonths: monthsBetween(row.startDate, now),
    isCurrent: row.isCurrent !== false,
    base,
    overtime,
    bonus,
    total: base + overtime + bonus,
  };
}

export function incomeSummary(
  app: LoanApplication,
  now = new Date()
): IncomeSummary {
  const rows = (app.employment || []).map((r) => incomeRow(r, now));
  const current = rows.filter((r) => r.isCurrent);
  const base = current.reduce((a, r) => a + r.base, 0);
  const variable = current.reduce((a, r) => a + r.overtime + r.bonus, 0);
  // History = earliest start date of any row to today (overlap-tolerant).
  const starts = rows
    .map((r) => r.tenureMonths)
    .filter((m): m is number => m !== null);
  return {
    rows,
    base,
    variable,
    employmentMonthly: base + variable,
    historyMonths: starts.length ? Math.max(...starts) : 0,
    selfEmployed: Boolean(app.declarations?.selfEmployed),
  };
}

/* ------------------------------------------------------------------ assets */

export type AssetCategory = 'liquid' | 'retirement' | 'gift' | 'other';

export function assetCategory(accountType: string): AssetCategory {
  const t = (accountType || '').toLowerCase();
  if (/(retire|401|\bira\b|403|pension)/.test(t)) return 'retirement';
  if (t.includes('gift')) return 'gift';
  if (
    /(check|saving|money market|\bcd\b|certificate|cash|brokerage|stock|bond|mutual)/.test(
      t
    )
  )
    return 'liquid';
  return 'other';
}

export interface AssetRow extends LoanAssetLine {
  category: AssetCategory;
  /** Value counted toward liquidity (retirement haircut applied). */
  usable: number;
}

export interface AssetSummary {
  rows: AssetRow[];
  total: number;
  usable: number;
  byCategory: Record<AssetCategory, number>;
}

export function assetSummary(app: LoanApplication): AssetSummary {
  const byCategory: Record<AssetCategory, number> = {
    liquid: 0,
    retirement: 0,
    gift: 0,
    other: 0,
  };
  const rows = (app.assetLines || [])
    .filter((a) => a && (a.institutionName || num(a.balance)))
    .map((a) => {
      const category = assetCategory(a.accountType);
      const balance = num(a.balance) ?? 0;
      byCategory[category] += balance;
      const usable =
        category === 'retirement' ? balance * RETIREMENT_HAIRCUT : balance;
      return { ...a, category, usable };
    });
  return {
    rows,
    total: rows.reduce((t, r) => t + (num(r.balance) ?? 0), 0),
    usable: rows.reduce((t, r) => t + r.usable, 0),
    byCategory,
  };
}

/* ------------------------------------------------------------- liabilities */

export function monthlyDebts(app: LoanApplication): number {
  return (app.liabilityLines || [])
    .filter((l) => !l.toBePaidOff)
    .reduce((t, l) => t + (num(l.monthlyPayment) ?? 0), 0);
}

/* ------------------------------------------------------------------ ratios */

const ratio = (a: number | null, b: number | null) =>
  a === null || !b ? null : a / b;

export function dtiStatus(
  value: number | null,
  kind: 'front' | 'back'
): RatioStatus {
  if (value === null) return 'unknown';
  const pct = value * 100;
  const [good, caution] = kind === 'front' ? [28, 36] : [36, 45];
  if (pct <= good) return 'good';
  if (pct <= caution) return 'caution';
  return 'high';
}

export function ltvStatus(
  value: number | null,
  investor: boolean
): RatioStatus {
  if (value === null) return 'unknown';
  const pct = value * 100;
  const [good, caution] = investor ? [70, 80] : [80, 95];
  if (pct <= good) return 'good';
  if (pct <= caution) return 'caution';
  return 'high';
}

export function dscrStatus(value: number | null): RatioStatus {
  if (value === null) return 'unknown';
  if (value >= 1.25) return 'good';
  if (value >= 1.0) return 'caution';
  return 'high';
}

export interface AgencyRatios {
  housing: HousingExpense;
  /** Gross monthly income used for DTI (incl. positive net rental). */
  qualifyingIncome: number;
  /** Monthly debts used for back-end DTI (incl. negative net rental). */
  qualifyingDebts: number;
  /** Subject net rental (75% of rent − PITIA); only for investment occupancy. */
  subjectNetRental: number | null;
  frontDti: number | null;
  backDti: number | null;
  ltv: number | null;
  downPayment: number | null;
  cashToClose: number | null;
  reservesMonths: number | null;
}

/**
 * Agency-style ratios. Owner-occupied: front = PITIA / income, back =
 * (PITIA + debts) / income. Investment occupancy: the subject's PITIA is
 * offset by 75% of its rent (positive net adds to income, negative to debts)
 * and front-end DTI doesn't apply.
 */
export function agencyRatios(
  app: LoanApplication,
  s: ReviewScenario,
  now = new Date()
): AgencyRatios {
  const housing = housingExpense(app, s);
  const income = incomeSummary(app, now).employmentMonthly;
  const debts = monthlyDebts(app);
  const occ = occupancyOf(app);
  const refinance = isRefinanceFile(app);
  const value = refinance
    ? num(app.agencyFields?.estimatedPropertyValue) ?? s.purchasePrice
    : s.purchasePrice;

  let qualifyingIncome = income;
  let qualifyingDebts = debts;
  let subjectNetRental: number | null = null;
  let frontDti: number | null = null;

  if (occ === 'investment') {
    const rent =
      num(app.property?.proposedMonthlyRent) ??
      num(app.dscrFields?.monthlyRent);
    if (rent !== null && housing.total !== null) {
      subjectNetRental = rent * RENTAL_INCOME_FACTOR - housing.total;
      if (subjectNetRental >= 0) qualifyingIncome += subjectNetRental;
      else qualifyingDebts += -subjectNetRental;
    } else if (housing.total !== null) {
      qualifyingDebts += housing.total;
    }
  } else {
    frontDti = ratio(housing.total, qualifyingIncome);
    if (housing.total !== null) qualifyingDebts += housing.total;
  }

  const backDti =
    housing.total === null ? null : ratio(qualifyingDebts, qualifyingIncome);
  const downPayment =
    !refinance && value !== null && s.loanAmount !== null
      ? Math.max(value - s.loanAmount, 0)
      : null;
  const closingCosts =
    value !== null ? (value * (s.closingCostPct || 0)) / 100 : 0;
  const cashToClose = refinance
    ? closingCosts
    : downPayment === null
    ? null
    : downPayment + closingCosts;
  const usable = assetSummary(app).usable;
  const reservesMonths =
    cashToClose === null || !housing.total
      ? null
      : (usable - cashToClose) / housing.total;

  return {
    housing,
    qualifyingIncome,
    qualifyingDebts,
    subjectNetRental,
    frontDti,
    backDti,
    ltv: ratio(s.loanAmount, value),
    downPayment,
    cashToClose,
    reservesMonths,
  };
}

/* ---------------------------------------------------------------- investor */

export interface ExperienceSummary {
  /** Track-record rows sold within the lookback window. */
  recentExits: number;
  /** All completed (sold) projects. */
  completedProjects: number;
  /** Track-record rows still held (no sale date). */
  heldProjects: number;
  /** Rentals on the PFS schedule of real estate owned. */
  rentalsOwned: number;
  /** Count used for the tier: recent exits + rentals currently owned. */
  verifiableCount: number;
  tier: 'Tier 1' | 'Tier 2' | 'Tier 3' | 'No experience';
  tierLabel: string;
  avgProfit: number | null;
  avgRoiPct: number | null;
  avgHoldMonths: number | null;
}

/**
 * Experience tier, the way bridge / fix-and-flip lenders commonly bucket it:
 * 5+ verifiable projects in 36 months → Tier 1, 3–4 → Tier 2, 1–2 → Tier 3.
 */
export function experienceSummary(
  app: LoanApplication,
  now = new Date()
): ExperienceSummary {
  const rows = app.trackRecord || [];
  const completed = rows.filter((r) => r.salesDate && num(r.salesPrice));
  const recent = completed.filter((r) => {
    const m = monthsBetween(r.salesDate, now);
    return m !== null && m <= EXPERIENCE_LOOKBACK_MONTHS;
  });
  const reo =
    (
      app as unknown as {
        pfsSnapshot?: { realEstateOwned?: { status?: string }[] };
      }
    ).pfsSnapshot?.realEstateOwned || [];
  const rentalsOwned = reo.filter((r) => r.status === 'rental').length;
  const verifiableCount = recent.length + rentalsOwned;

  const profits = completed.map((r) => {
    const cost = (num(r.purchasePrice) ?? 0) + (num(r.renovationBudget) ?? 0);
    const profit = (num(r.salesPrice) ?? 0) - cost;
    return { profit, roi: cost ? (profit / cost) * 100 : null };
  });
  const holds = completed
    .map((r) => monthsBetween(r.purchaseDate, r.salesDate))
    .filter((m): m is number => m !== null);
  const avg = (xs: number[]) =>
    xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;

  const tier =
    verifiableCount >= 5
      ? 'Tier 1'
      : verifiableCount >= 3
      ? 'Tier 2'
      : verifiableCount >= 1
      ? 'Tier 3'
      : 'No experience';
  const tierLabel = {
    'Tier 1': '5+ projects in 36 months',
    'Tier 2': '3–4 projects in 36 months',
    'Tier 3': '1–2 projects in 36 months',
    'No experience': 'No verifiable projects in 36 months',
  }[tier];

  return {
    recentExits: recent.length,
    completedProjects: completed.length,
    heldProjects: rows.length - completed.length,
    rentalsOwned,
    verifiableCount,
    tier,
    tierLabel,
    avgProfit: avg(profits.map((p) => p.profit)),
    avgRoiPct: avg(
      profits.map((p) => p.roi).filter((r): r is number => r !== null)
    ),
    avgHoldMonths: avg(holds),
  };
}

export interface InvestorMetrics {
  housing: HousingExpense;
  monthlyRent: number | null;
  /** Rent / PITIA — the common DSCR-loan definition. */
  dscr: number | null;
  /** NOI-based DSCR saved on the file by the borrower-side calculator. */
  dscrNoi: number | null;
  ltv: number | null;
  /** Loan / (price + rehab). */
  ltc: number | null;
  /** Loan / after-repair value. */
  arvLtv: number | null;
  rehabBudget: number | null;
  arv: number | null;
  holdMonths: number | null;
  /** ARV − price − rehab − carry − selling costs − closing costs. */
  projectedProfit: number | null;
  projectedRoiPct: number | null;
  carryingCost: number | null;
  cashToClose: number | null;
  /** Usable assets left after cash to close, in months of PITIA. */
  reservesMonths: number | null;
}

export function investorMetrics(
  app: LoanApplication,
  s: ReviewScenario
): InvestorMetrics {
  const housing = housingExpense(app, s);
  const ip = app.investorProgramFields || {};
  const loanInfo = (product(app)?.loanInfo || {}) as Record<string, unknown>;
  const monthlyRent =
    num(app.dscrFields?.monthlyRent) ??
    num(app.property?.proposedMonthlyRent) ??
    num(loanInfo.rent);
  const rehab = num(ip.rehabBudget) ?? num(loanInfo.rehab);
  const arv = num(ip.afterRepairValue) ?? num(loanInfo.arv);
  const holdMonths = num(ip.holdPeriodMonths);
  const price = isRefinanceFile(app)
    ? num(app.agencyFields?.estimatedPropertyValue) ?? s.purchasePrice
    : s.purchasePrice;

  const closingCosts =
    price !== null ? (price * (s.closingCostPct || 0)) / 100 : null;
  const carryingCost =
    holdMonths !== null && housing.total !== null
      ? housing.total * holdMonths
      : null;
  const projectedProfit =
    arv !== null && price !== null
      ? arv -
        price -
        (rehab ?? 0) -
        (carryingCost ?? 0) -
        (arv * SELLING_COST_PCT) / 100 -
        (closingCosts ?? 0)
      : null;
  const totalCost =
    price !== null ? price + (rehab ?? 0) + (closingCosts ?? 0) : null;
  // Cash in: down payment + closing costs + whatever rehab the loan doesn't cover.
  const cashToClose =
    price !== null && s.loanAmount !== null
      ? Math.max(price + (rehab ?? 0) - s.loanAmount, 0) + (closingCosts ?? 0)
      : null;
  const usable = assetSummary(app).usable;

  return {
    housing,
    monthlyRent,
    dscr: ratio(monthlyRent, housing.total),
    dscrNoi: num(app.dscrFields?.computed?.dscr),
    ltv: ratio(s.loanAmount, price),
    ltc: ratio(s.loanAmount, price === null ? null : price + (rehab ?? 0)),
    arvLtv: ratio(s.loanAmount, arv),
    rehabBudget: rehab,
    arv,
    holdMonths,
    projectedProfit,
    projectedRoiPct:
      projectedProfit !== null && totalCost
        ? (projectedProfit / totalCost) * 100
        : null,
    carryingCost,
    cashToClose,
    reservesMonths:
      cashToClose === null || !housing.total
        ? null
        : (usable - cashToClose) / housing.total,
  };
}

/* ---------------------------------------------------------------- findings */

export interface ReviewFinding {
  severity: 'error' | 'warning' | 'info';
  area: 'income' | 'assets' | 'credit' | 'property' | 'borrower' | 'experience';
  message: string;
}

const declYes = (v: unknown) => String(v ?? '').toLowerCase() === 'yes';

/** Things a reviewer should look at before moving the file forward. */
export function reviewFindings(
  app: LoanApplication,
  s: ReviewScenario,
  now = new Date()
): ReviewFinding[] {
  const out: ReviewFinding[] = [];
  const investor = isInvestorFile(app);
  const income = incomeSummary(app, now);
  const d = app.declarations || {};

  (app.borrowers || []).forEach((b) => {
    const name =
      [b.firstName, b.lastName].filter(Boolean).join(' ') || 'Borrower';
    if (String(b.ssn || '').replace(/\D/g, '').length !== 9) {
      out.push({
        severity: 'error',
        area: 'borrower',
        message: `${name}: SSN missing or invalid.`,
      });
    }
  });
  if (!app.property?.subjectPropertyAddress) {
    out.push({
      severity: 'error',
      area: 'property',
      message: 'Subject property address is missing.',
    });
  }
  if (s.interestRatePct === null) {
    out.push({
      severity: 'warning',
      area: 'property',
      message:
        'No note rate on file — payment uses the scenario rate you enter.',
    });
  }

  if (!investor) {
    if (!income.employmentMonthly) {
      out.push({
        severity: 'error',
        area: 'income',
        message: 'No current employment income entered.',
      });
    } else if (income.historyMonths < 24) {
      out.push({
        severity: 'warning',
        area: 'income',
        message: `Only ${income.historyMonths} months of employment history (24 typically required).`,
      });
    }
    if (income.variable > 0) {
      out.push({
        severity: 'info',
        area: 'income',
        message:
          'Overtime / bonus income — confirm a 2-year history before using it.',
      });
    }
    if (income.selfEmployed) {
      out.push({
        severity: 'info',
        area: 'income',
        message:
          'Self-employed — 2 years of tax returns needed to verify income.',
      });
    }
    const r = agencyRatios(app, s, now);
    if (r.backDti !== null && r.backDti > 0.5) {
      out.push({
        severity: 'error',
        area: 'income',
        message: `Back-end DTI ${(r.backDti * 100).toFixed(1)}% exceeds 50%.`,
      });
    } else if (r.backDti !== null && r.backDti > 0.45) {
      out.push({
        severity: 'warning',
        area: 'income',
        message: `Back-end DTI ${(r.backDti * 100).toFixed(1)}% is above 45%.`,
      });
    }
    if (r.reservesMonths !== null && r.reservesMonths < 0) {
      out.push({
        severity: 'error',
        area: 'assets',
        message: 'Entered assets do not cover the estimated cash to close.',
      });
    } else if (r.reservesMonths !== null && r.reservesMonths < 2) {
      out.push({
        severity: 'warning',
        area: 'assets',
        message: `Under 2 months of reserves after closing (${r.reservesMonths.toFixed(
          1
        )}).`,
      });
    }
  } else {
    const m = investorMetrics(app, s);
    if (m.dscr !== null && m.dscr < 1) {
      out.push({
        severity: app.productType === 'dscr' ? 'error' : 'warning',
        area: 'property',
        message: `DSCR ${m.dscr.toFixed(2)} — rent does not cover PITIA.`,
      });
    }
    if (app.productType === 'dscr' && m.monthlyRent === null) {
      out.push({
        severity: 'error',
        area: 'property',
        message: 'DSCR file without market / lease rent.',
      });
    }
    if (app.productType === 'bridge' && m.arv === null) {
      out.push({
        severity: 'warning',
        area: 'property',
        message: 'Bridge file without an after-repair value.',
      });
    }
    if (m.reservesMonths !== null && m.reservesMonths < 0) {
      out.push({
        severity: 'error',
        area: 'assets',
        message: 'Entered assets do not cover the estimated cash to close.',
      });
    } else if (m.reservesMonths !== null && m.reservesMonths < 6) {
      out.push({
        severity: 'warning',
        area: 'assets',
        message: `Under 6 months of reserves after closing (${m.reservesMonths.toFixed(
          1
        )}).`,
      });
    }
    const exp = experienceSummary(app, now);
    if (app.productType === 'bridge' && exp.verifiableCount === 0) {
      out.push({
        severity: 'warning',
        area: 'experience',
        message: 'No verifiable project experience — expect lower leverage.',
      });
    }
  }

  if (!assetSummary(app).rows.length) {
    out.push({
      severity: 'warning',
      area: 'assets',
      message: 'No assets entered.',
    });
  }
  if (
    declYes(d.bankruptcy) ||
    declYes(d.foreclosure) ||
    declYes(d.shortSale) ||
    declYes(d.deedInLieu)
  ) {
    out.push({
      severity: 'warning',
      area: 'credit',
      message:
        'Prior bankruptcy / foreclosure / short sale declared — check seasoning.',
    });
  }
  if (declYes(d.borrowedDownPayment)) {
    out.push({
      severity: 'warning',
      area: 'assets',
      message:
        'Down payment is borrowed — must be secured and counted as a debt.',
    });
  }
  if (declYes(d.giftDownPayment)) {
    out.push({
      severity: 'info',
      area: 'assets',
      message: 'Gift funds — gift letter and transfer evidence needed.',
    });
  }
  if (declYes(d.judgmentLien) || declYes(d.partyToLawsuit)) {
    out.push({
      severity: 'warning',
      area: 'credit',
      message: 'Judgment / lawsuit declared.',
    });
  }
  return out;
}
